// GPT-Live の data channel のイベントを読み書きする (spec 2026-09-14 §3.4 / §3.5)。
//
// **WebRTC から切り離してある** — 送る先は `send` として受け、受け取ったメッセージは
// `handle` に渡してもらう。こうすると、data channel に**何を送ったか**を単体テストで
// 直に検証できる (2.1 は engine のテストがアプリ側の入口からしか書かれておらず、
// WebSocket に何を送ったかを 1 つも見ていなかったため、「実装が丸ごと無い」欠陥を
// 3 件見逃した — spec 2026-08-29 §7.4)。
//
// イベントの形は openai-python 3.13.0 の types/live/ と、spike で実際に届いたキー
// (docs/research/gpt-live/ の記録) に合わせてある。

const ERROR_MSG_CAP = 200;

/**
 * `?realtime-debug=1` のときだけ送受信と判断を console に出す (既定 off、production は無音)。
 * **live の失敗も R2 には何も残らない**ので、ここが唯一の観測点になる (spec L16)。
 * 2.1 と同じフラグを共用する。module-top const にしない (テストが URL を差し替えられるように)。
 */
function debugOn(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("realtime-debug") === "1";
}

export function liveDebug(msg: string, detail?: unknown): void {
  if (!debugOn()) return;
  if (detail === undefined) console.log(`[live] ${msg}`);
  else console.log(`[live] ${msg}`, detail);
}

export type LiveDelegation = {
  id: string;
  target: string;
  /** delegation が作られた位置 (サーバのセッション時間軸、ms) */
  offsetMs: number | null;
  /** こちらのコマンドに応じて作られたとき、そのコマンドの event_id */
  clientEventId: string | null;
};

export type LiveFunctionCall = {
  name: string;
  args: string;
  callId: string;
  delegationId: string | null;
};

export type LiveBackendUsage = { input: number; cached: number; output: number };

export type LiveBackendDone = {
  delegationId: string | null;
  status: string | null;
  usage: LiveBackendUsage | null;
};

export type LiveClientOpts = {
  send: (payload: Record<string, unknown>) => void;
  onSessionStarted?: (sessionId: string | null) => void;
  onInstructionsAppended?: (clientEventId: string | null, startMs: number | null) => void;
  onInputTranscript?: (delta: string, startMs: number | null, endMs: number | null) => void;
  onOutputTranscript?: (delta: string, startMs: number | null, endMs: number | null) => void;
  onDelegationCreated?: (d: LiveDelegation) => void;
  onFunctionCall?: (c: LiveFunctionCall) => void;
  onBackendResponseDone?: (d: LiveBackendDone) => void;
  /** 累積の秒数 (足し合わせない) */
  onUsage?: (seconds: number) => void;
  onClosed?: (reason: string | null, seconds: number | null) => void;
  onError?: (e: { code: string; message: string }) => void;
};

// backend の応答の終わり。spike では tool を含む応答も、tool の結果を返した後の続きも、
// どちらも response.completed で終わった
const DONE_TYPES = new Set([
  "response.completed",
  "response.done",
  "response.incomplete",
  "response.failed",
]);

const num = (v: unknown): number | null => (typeof v === "number" ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
/** Responses の message item の本文 (content の text をつなげる) */
const messageText = (content: unknown): string =>
  Array.isArray(content)
    ? content.map((c) => str((c as { text?: unknown } | null)?.text) ?? "").join("")
    : "";

export class LiveClient {
  private seq = 0;

  constructor(private opts: LiveClientOpts) {}

  private nextId(prefix: string): string {
    this.seq += 1;
    return `${prefix}_${this.seq}`;
  }

  private send(payload: Record<string, unknown>): void {
    liveDebug(`→ ${String(payload.type)}`);
    try {
      this.opts.send(payload);
    } catch {
      // 送れなくても呼び出し元 (タイマーやイベントのコールバック) は止めない
    }
  }

  /** data channel から届いたメッセージ (文字列でも parse 済みでも受ける)。壊れていれば捨てる */
  handle(raw: unknown): void {
    let msg: Record<string, unknown>;
    if (typeof raw === "string") {
      try {
        msg = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        return;
      }
    } else if (raw && typeof raw === "object") {
      msg = raw as Record<string, unknown>;
    } else {
      return;
    }
    const type = str(msg.type) ?? "";
    switch (type) {
      case "session.started": {
        const s = (msg.session ?? {}) as { id?: unknown };
        liveDebug("← session.started");
        this.opts.onSessionStarted?.(str(s.id));
        return;
      }
      case "session.instructions.appended":
        liveDebug("← instructions.appended", msg.client_event_id);
        this.opts.onInstructionsAppended?.(str(msg.client_event_id), num(msg.start_ms));
        return;
      case "session.input_transcript.delta":
        liveDebug("← heard", msg.delta);
        this.opts.onInputTranscript?.(str(msg.delta) ?? "", num(msg.start_ms), num(msg.end_ms));
        return;
      case "session.output_transcript.delta":
        this.opts.onOutputTranscript?.(str(msg.delta) ?? "", num(msg.start_ms), num(msg.end_ms));
        return;
      case "session.delegation.created": {
        const d = (msg.delegation ?? {}) as { id?: unknown; target?: unknown };
        const delegation: LiveDelegation = {
          id: str(d.id) ?? "",
          target: str(d.target) ?? "",
          offsetMs: num(msg.offset_ms),
          clientEventId: str(msg.client_event_id),
        };
        liveDebug("← delegation.created", delegation);
        this.opts.onDelegationCreated?.(delegation);
        return;
      }
      case "response.event": {
        const inner = (msg.event ?? {}) as Record<string, unknown>;
        const it = str(inner.type) ?? "";
        const delegationId = str(msg.delegation_id);
        if (it === "response.output_item.done") {
          const item = (inner.item ?? {}) as Record<string, unknown>;
          if (item.type === "message") {
            // backend の返事の本文。live モデルはこれを受けて話すので、「記録しました」と言ったのに
            // 記録されていない回の理由を追う手がかりになる (2026-09-14 の実機で要った)
            liveDebug("← backend said", messageText(item.content));
            return;
          }
          if (item.type !== "function_call") return;
          const call: LiveFunctionCall = {
            name: str(item.name) ?? "",
            args: str(item.arguments) ?? "",
            callId: str(item.call_id) ?? "",
            delegationId,
          };
          // **type だけの行では tool が埋もれる** (2.1 の §7.4)。名前と引数まで出す
          liveDebug(`← tool ${call.name}`, call.args);
          this.opts.onFunctionCall?.(call);
          return;
        }
        if (DONE_TYPES.has(it)) {
          const resp = (inner.response ?? {}) as { status?: unknown; usage?: unknown };
          const u = resp.usage as
            | {
                input_tokens?: unknown;
                output_tokens?: unknown;
                input_tokens_details?: { cached_tokens?: unknown };
              }
            | undefined;
          const usage: LiveBackendUsage | null = u
            ? {
                input: num(u.input_tokens) ?? 0,
                cached: num(u.input_tokens_details?.cached_tokens) ?? 0,
                output: num(u.output_tokens) ?? 0,
              }
            : null;
          liveDebug(`← backend ${it}`, { delegationId, status: resp.status });
          this.opts.onBackendResponseDone?.({ delegationId, status: str(resp.status), usage });
        }
        return;
      }
      case "session.usage.updated": {
        const s = num((msg.usage as { seconds?: unknown } | undefined)?.seconds);
        if (s !== null) this.opts.onUsage?.(s);
        return;
      }
      case "session.closed": {
        const s = num((msg.usage as { seconds?: unknown } | undefined)?.seconds);
        liveDebug("← session.closed", msg.reason);
        this.opts.onClosed?.(str(msg.reason), s);
        return;
      }
      case "error": {
        const e = (msg.error ?? {}) as { code?: unknown; message?: unknown };
        const err = {
          code: str(e.code) ?? "error",
          message: (str(e.message) ?? "").slice(0, ERROR_MSG_CAP),
        };
        liveDebug("← error", err);
        this.opts.onError?.(err);
        return;
      }
      default:
        return;
    }
  }

  /**
   * 話させたいことを頼む。**頼むだけで、その通りに言う保証は無い** (公式の注記)。
   * 返す event_id は session.instructions.appended の client_event_id と突き合わせる。
   */
  appendInstructions(content: string): string {
    const id = this.nextId("instr");
    this.send({ type: "session.instructions.append", event_id: id, delegation_id: null, content });
    return id;
  }

  /**
   * tool の結果を返し、backend に続きを作らせる。**返さないと backend が止まる**。
   * **順序が仕様** — 先に response.create を送ると、結果の無いまま続きを作る。
   */
  sendToolOutput(callId: string, output: string): void {
    this.send({
      type: "response.item.create",
      event_id: this.nextId("out"),
      item: { type: "function_call_output", call_id: callId, output },
    });
    this.send({ type: "response.create", event_id: this.nextId("cont") });
  }

  /**
   * アプリから backend に直接頼む (live モデルの delegation を経ない。二重の網のアプリの経路)。
   * 返す event_id は、直後の session.delegation.created の client_event_id と突き合わせる。
   */
  askBackend(text: string): string {
    this.send({
      type: "response.item.create",
      event_id: this.nextId("ask"),
      item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
    });
    const id = this.nextId("ask_go");
    this.send({ type: "response.create", event_id: id });
    return id;
  }

  /** セッションを閉じる。閉じ忘れたセッションは $0.05/分で課金され続ける */
  close(): void {
    this.send({ type: "session.close", event_id: this.nextId("close") });
  }
}
